// Web adapter over the shared, platform-neutral contract layer.
//
// The reusable client — the fetch+Bearer request helper, the 401→clear
// behaviour, every API method, the auth-aware SSE reader, and the SpacetimeDB
// cell decoders — now lives in `modules/shared` so a future React-Native app
// can import it verbatim. This file is the WEB half of that seam: it wires the
// shared client with a `localStorage`-backed `TokenStore` (see
// `webTokenStore.ts`), the build-time backend origin (`VITE_API_URL`), and the
// PostHog identify/reset side-effects that are a web presentation concern.
//
// It then re-exports the shared client's methods as the same standalone
// functions the app has always imported (`createThread`, `getHours`,
// `subscribeChat`, …) plus the shared wire types, so no call site changes and
// the web app behaves EXACTLY as before. The pre-pivot agent-platform methods
// were removed in the pivot purge; re-add from history if a later wave needs
// them.
//
// Session is stored in localStorage under `wt_token` / `wt_identity` /
// `wt_user` (unchanged), via the injected web TokenStore.

import { createClient, ApiError } from "@waytide/core";
import type { AuthResponse } from "@waytide/core";
import { webTokenStore } from "./webTokenStore";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "http://localhost:3001";

// The single web-wired client: shared contract + localStorage store + the
// web origin + the PostHog hooks. `onSessionSaved` / `onSessionCleared`
// preserve the historical lazy-imported analytics side-effects that used to
// live inside saveSession / clearSession, kept OUT of the neutral layer.
const client = createClient({
  apiBase: API_BASE,
  store: webTokenStore,
  onSessionSaved: (resp: AuthResponse) => {
    void import("../observability/posthog").then((m) =>
      m.identifyOrchestrator(resp.identity, resp.user.email),
    );
  },
  onSessionCleared: () => {
    void import("../observability/posthog").then((m) => m.resetOrchestrator());
  },
});

// ─── Re-exported shared types (the wire contract) ────────────────────
// Every type the app imported from this module, now sourced from shared so
// there is a single definition web + RN share.
export type {
  SessionUser,
  AuthResponse,
  ShareAuthResponse,
  OAuthProvider,
  GithubRepo,
  GithubReposResponse,
  BindRepoResponse,
  BoundRepoResponse,
  SupervisorEvent,
  SupervisorEventsResponse,
  ArbiterEvent,
  ArbiterEventsResponse,
  SendDigestResponse,
  Thread,
  ChatMessage,
  ThreadMember,
  ThreadMembersResponse,
  PauseResponse,
  SessionPresence,
  NeedsInputReason,
  FocusState,
  PresenceHeartbeatRequest,
  SteeringOption,
  SteeringDecisionBody,
  SteeringDecision,
  SteeringResolved,
  SteeringPick,
  PresenceResponse,
  ProjectSummary,
  ProjectsResponse,
  AssignProjectResponse,
  ProjectGoalsResponse,
  Appearance,
  PreferencesResponse,
  SavePreferencesRequest,
  ClearRuleRequest,
  ClearRuleResponse,
  TraceStreamEvent,
  SseEvent,
  HoursLedgerEntry,
  HoursResponse,
  TopupResponse,
  OperatorAuthority,
  OperatorConfigResponse,
  SaveOperatorConfigRequest,
  RuleAuthority,
  ContactFrequency,
  RuleOperator,
  RuleScopeKind,
  OperatorRule,
  ResolvedRuleset,
  SetRuleRequest,
  TraceEvent,
  TraceEvents,
  RadioPolicy,
  RadioBudget,
  RadioClip,
  BalanceResponse,
  SessionSpend,
  SessionSpendResponse,
  TopupCheckoutResponse,
  Phase,
  Receipt,
  PhaseWithReceipt,
  PhasesResponse,
  ShadowRun,
  ShadowRunsResponse,
  StreamHealth,
} from "@waytide/core";

// ─── Re-exported shared helpers (pure, platform-neutral) ─────────────
export {
  ApiError,
  parseSseBuffer,
  decodeIdentity,
  decodeOptionNumber,
  normalizeOperatorConfig,
} from "@waytide/core";

// ─── Session (bound to the web localStorage store) ───────────────────

export function getToken(): string | null {
  return client.getToken();
}

export function getIdentity(): string | null {
  return client.getIdentity();
}

export function getUser(): import("@waytide/core").SessionUser | null {
  return client.getUser();
}

export function saveSession(resp: AuthResponse): void {
  client.saveSession(resp);
}

export function clearSession(): void {
  client.clearSession();
}

// ─── Auth ────────────────────────────────────────────────────────────

/** Exchange a thread share token (carried in a deep link) for a thread-scoped
 * JWT, per the M1 `POST /auth/share` contract. The returned token is good only
 * for that one thread's messages + SSE. Not used by the web dashboard's full
 * sign-in today; exposed here so the same surface backs the App-Clip / RN
 * instant-join flow. */
export function authShare(
  share_token: string,
): Promise<import("@waytide/core").ShareAuthResponse> {
  return client.authShare(share_token);
}

export function authMe(): Promise<{
  identity: string;
  email: string;
  display_name: string;
  is_admin: boolean;
}> {
  return client.authMe();
}

// ─── OAuth sign-in (Google / Apple) ──────────────────────────────────

/** The backend URL the browser navigates to, to start OAuth sign-in with
 * `provider` (Google or Apple — GitHub is a per-project connection, never
 * sign-in). A full-page redirect, not a fetch; the callback returns to
 * `/signin#token=…`. The web omits `redirect` (the backend's default web
 * return); only the desktop passes its deep link. The shared client also
 * carries `claimDeviceCode` (the paste-a-code fallback), but that is a
 * DESKTOP-only surface — the web never renders it, so it is not re-exported
 * here. */
export function oauthStartUrl(
  provider: import("@waytide/core").OAuthProvider,
  redirect?: string,
): string {
  return client.oauthStartUrl(provider, redirect);
}

// ─── GitHub repo connection + repo-bind (per-project, NOT sign-in) ───

/** The PROJECT's connected GitHub account's repositories (its token, decrypted
 * server-side), to drive the mandatory repo pick when connecting a session.
 * 409 (`github_not_connected`) until the project has a connection. */
export function listProjectGithubRepos(
  project_id: number,
): Promise<import("@waytide/core").GithubReposResponse> {
  return client.listProjectGithubRepos(project_id);
}

/** The GitHub authorize URL to open in a browser to connect the project's
 * GitHub account (owner-gated mint; the signed state carries the project). */
export function githubConnectUrl(
  project_id: number,
): Promise<import("@waytide/core").GithubConnectUrlResponse> {
  return client.githubConnectUrl(project_id);
}

/** Bind a chosen GitHub repo to a session — the mandatory repo-bind (B2).
 * Membership-scoped server-side on the caller's JWT. */
export function bindSessionRepo(
  thread_id: number,
  repo: import("@waytide/core").GithubRepo,
): Promise<import("@waytide/core").BindRepoResponse> {
  return client.bindSessionRepo(thread_id, {
    owner: repo.owner,
    name: repo.name,
    full_name: repo.full_name,
    default_branch: repo.default_branch,
  });
}

/** Read a session's repo binding; `bound:false` when not yet bound. */
export function getBoundRepo(
  thread_id: number,
): Promise<import("@waytide/core").BoundRepoResponse> {
  return client.getBoundRepo(thread_id);
}

/** The PR desk (founder, 2026-07-29): every DRAFT + OPEN pull request across the
 * caller's projects, each answering "do your eyes need this?" — default no.
 * Newest-updated first; closed work is deliberately absent. */
export function listPullRequestDesk(): Promise<
  import("@waytide/core").PullRequestDeskResponse
> {
  return client.listPullRequestDesk();
}

/** The bound repo's project schematic (Brief 5 / pt7) — the structural map the
 * read/write transparency surface renders. */
export function getSchematic(
  thread_id: number,
): Promise<import("@waytide/core").SchematicResponse> {
  return client.getSchematic(thread_id);
}

// ─── Supervisor catches ────────────────────────────────────────────────────

export function listSupervisorEvents(): Promise<
  import("@waytide/core").SupervisorEventsResponse
> {
  return client.listSupervisorEvents();
}

// ─── Arbiter suggestions ────────────────────────────────────────────────

export function listArbiterEvents(): Promise<
  import("@waytide/core").ArbiterEventsResponse
> {
  return client.listArbiterEvents();
}

// ─── Signal digest ───────────────────────────────────────────────────

/** Send the signed-in orchestrator a digest now (email + spoken-audio summary
 * of recent catches + suggestions) and return the counts it covered. */
export function sendDigest(): Promise<import("@waytide/core").SendDigestResponse> {
  return client.sendDigest();
}

// ─── Threads + chat ──────────────────────────────────────────────────

/** Create a thread (and seed its members) as the signed-in orchestrator,
 * returning the server-stamped row. The creator is derived from the JWT, so
 * `member_emails` carries only the OTHER members; the server de-dups and
 * always adds the caller. */
export function createThread(
  kind: import("@waytide/core").Thread["kind"],
  title: string,
  member_emails: string[],
): Promise<import("@waytide/core").Thread> {
  return client.createThread(kind, title, member_emails);
}

/** List the caller's threads (newest first; the caller renders as given). */
export function listThreads(): Promise<{
  threads: import("@waytide/core").Thread[];
}> {
  return client.listThreads();
}

/** Send a message to a thread the caller is a member of and return the
 * persisted row (server-stamped id + created_at + sender). `idempotencyKey`
 * (Brief 5 / point 15) is forwarded as the `Idempotency-Key` header so an
 * outbox replay can be de-duped server-side; omit it for the unchanged call. */
export function sendThreadMessage(
  thread_id: number,
  body: string,
  aside?: boolean,
  idempotencyKey?: string,
): Promise<import("@waytide/core").ChatMessage> {
  return client.sendThreadMessage(thread_id, body, aside, idempotencyKey);
}

/** List a thread's messages (oldest→newest is the thread order). */
export function listThreadMessages(
  thread_id: number,
): Promise<{ messages: import("@waytide/core").ChatMessage[] }> {
  return client.listThreadMessages(thread_id);
}

// ─── Thread members ──────────────────────────────────────────────────

/** List a thread's members. Membership-scoped server-side on the caller's
 * JWT (only a member can read the roster). */
export function listThreadMembers(
  thread_id: number,
): Promise<import("@waytide/core").ThreadMembersResponse> {
  return client.listThreadMembers(thread_id);
}

/** Invite a human to a thread by email and return the refreshed roster.
 * Idempotent — re-adding an existing member is a no-op success. */
export function addThreadMember(
  thread_id: number,
  member_email: string,
  member_kind?: string,
): Promise<import("@waytide/core").ThreadMembersResponse> {
  return client.addThreadMember(thread_id, member_email, member_kind);
}

// ─── Pause / resume ──────────────────────────────────────────────────

/** Pause the given agent_session — drives the bridge's verified halt and
 * posts the `::waytide-halt::` control message into the thread. */
export function pauseSession(
  thread_id: number,
): Promise<import("@waytide/core").PauseResponse> {
  return client.pauseSession(thread_id);
}

/** Resume the given agent_session — drives the bridge's verified resume and
 * posts the `::waytide-resume::` control message into the thread. */
export function resumeSession(
  thread_id: number,
): Promise<import("@waytide/core").PauseResponse> {
  return client.resumeSession(thread_id);
}

// ─── Chat SSE stream ─────────────────────────────────────────────────

/** Open the chat SSE stream and dispatch live `message` / `thread` events
 * until unsubscribed. Auto-reconnects with capped backoff; the returned
 * function aborts the in-flight request and halts reconnection. The optional
 * `onHealth` reports connection health (Brief 5 stream-honesty) — additive, so
 * callers that don't surface it omit it. */
export function subscribeChat(
  onMessage: (m: import("@waytide/core").ChatMessage) => void,
  onThread: (t: import("@waytide/core").Thread) => void,
  onPresence?: (p: import("@waytide/core").SessionPresence) => void,
  onTrace?: (t: import("@waytide/core").TraceStreamEvent) => void,
  onPreferences?: (p: import("@waytide/core").PreferencesResponse) => void,
  onBrain?: (b: import("@waytide/core").BrainEvent) => void,
  onHealth?: (h: import("@waytide/core").StreamHealth) => void,
): () => void {
  return client.subscribeChat(
    onMessage,
    onThread,
    onPresence,
    onTrace,
    onPreferences,
    onBrain,
    onHealth,
  );
}

// ─── Session presence (Brief 3 D1) ───────────────────────────────────

/** Every presence row for the caller's threads (the live status spine the
 * orchestration glance reads), newest-updated first. */
export function getPresence(): Promise<import("@waytide/core").PresenceResponse> {
  return client.getPresence();
}

// ─── Adherence insight (the corpus aggregate) ────────────────────────

/** The caller's ingested-session corpus reduced to the divergence/compaction
 * -vs-context thesis (the insight view renders this). Owner-scoped server-side. */
export function getInsight(): Promise<import("@waytide/core").InsightResponse> {
  return client.getInsight();
}

/** List the caller's ingested sessions (metadata) — the corpus index the browse
 * surface renders. Owner-scoped server-side. */
export function getIngestedSessions(): Promise<
  import("@waytide/core").IngestedSessionsResponse
> {
  return client.getIngestedSessions();
}

/** Fetch ONE ingested session's FULL raw transcript as text. The endpoint
 * returns the stored gzip with `Content-Encoding: gzip`, so the browser inflates
 * it transparently to the original `.jsonl`. Web-local (text, not JSON, so it
 * bypasses the JSON request helper) but carries the Bearer token like every
 * authed call. */
export async function getRawSession(sourceSessionId: string): Promise<string> {
  const token = getToken();
  const resp = await fetch(
    `${API_BASE}/ingest/sessions/${encodeURIComponent(sourceSessionId)}/raw`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!resp.ok) {
    throw new ApiError(resp.status, resp.statusText, null);
  }
  return resp.text();
}

// ─── Projects (Brief 4 / P8 — the Dashboard) ─────────────────────────

/** The caller's projects (the explicit, named entities the Dashboard groups
 * sessions under), newest first. */
export function listProjects(): Promise<import("@waytide/core").ProjectsResponse> {
  return client.listProjects();
}

/** Create a project owned by the signed-in orchestrator and return the
 * refreshed list (so the sidebar updates from one call). The owner is the JWT
 * email, never a body field. */
export function createProject(
  name: string,
): Promise<import("@waytide/core").ProjectsResponse> {
  return client.createProject(name);
}

/** Rename a project the caller owns; returns the refreshed list. */
export function renameProject(
  projectId: number,
  name: string,
): Promise<import("@waytide/core").ProjectsResponse> {
  return client.renameProject(projectId, name);
}

/** Assign a session (agent_session thread) to a project. Membership-scoped
 * server-side on the caller's JWT; the assignment merges onto the session's
 * presence row as `project_id`. */
export function assignProject(
  thread_id: number,
  project_id: number,
): Promise<import("@waytide/core").AssignProjectResponse> {
  return client.assignSessionProject(thread_id, project_id);
}

/** Report this surface's user focus (Brief 5 pt16). Fire-and-forget — the
 * backend stamps it against the verified JWT so it knows whether the user is
 * reachable in-app right now. */
export function postFocusHeartbeat(
  focus: import("@waytide/core").FocusState,
): Promise<void> {
  return client.postFocusHeartbeat(focus);
}

/** Record the user's pick on a steering decision (Brief 5 pt17): an option id
 * or typed `other` free-text. The chosen steer posts as the user's own message
 * and the card collapses across surfaces. */
export function pickSteeringDecision(
  decisionId: number,
  pick: import("@waytide/core").SteeringPick,
): Promise<void> {
  return client.pickSteeringDecision(decisionId, pick);
}

/** Read a project's immovable goals/spec (the alignment anchor) as markdown.
 * Membership-scoped server-side; an unset doc comes back with markdown "" and
 * updated_at 0. */
export function getProjectGoals(
  project_id: number,
): Promise<import("@waytide/core").ProjectGoalsResponse> {
  return client.getProjectGoals(project_id);
}

/** Author/replace a project's immovable goals and return the saved document.
 * Owner-gated server-side. */
export function putProjectGoals(
  project_id: number,
  markdown: string,
): Promise<import("@waytide/core").ProjectGoalsResponse> {
  return client.putProjectGoals(project_id, markdown);
}

/** The project's wiki — the second brain Signal builds (Brief 5). Owner-gated. */
export function getProjectWiki(
  project_id: number,
): Promise<import("@waytide/core").ProjectWikiResponse> {
  return client.getProjectWiki(project_id);
}

/** Signal's async questions for the project, with answers (Brief 5). Owner-gated. */
export function getProjectQuestions(
  project_id: number,
): Promise<import("@waytide/core").ProjectQuestionsResponse> {
  return client.getProjectQuestions(project_id);
}

// ─── User preferences (Brief 4 / P0b) ────────────────────────────────

/** The caller's saved UI preferences (appearance), persisted server-side and
 * synced across surfaces. Defaults to `system` when never saved. */
export function getPreferences(): Promise<
  import("@waytide/core").PreferencesResponse
> {
  return client.getPreferences();
}

/** Save the caller's appearance and return the saved value. On success the
 * backend fans a USER-scoped `preferences` SSE event to the caller's OTHER open
 * surfaces (never another operator's). */
export function putPreferences(
  appearance: import("@waytide/core").Appearance,
): Promise<import("@waytide/core").PreferencesResponse> {
  return client.putPreferences(appearance);
}

// ─── Prepaid hours ───────────────────────────────────────────────────

export function getHours(): Promise<import("@waytide/core").HoursResponse> {
  return client.getHours();
}

/** Credit the signed-in orchestrator's prepaid balance by `hours` and return
 * the new balance. Manual/dev top-up — the stand-in for Stripe. */
export function topUpHours(
  hours: number,
): Promise<import("@waytide/core").TopupResponse> {
  return client.topUpHours(hours);
}

// ─── Operator config ─────────────────────────────────────────────────

export function getOperatorConfig(): Promise<
  import("@waytide/core").OperatorConfigResponse
> {
  return client.getOperatorConfig();
}

/** Save the signed-in orchestrator's operator config and return the saved
 * (re-read) config, including the server-stamped `updated_at`. */
export function saveOperatorConfig(
  cfg: import("@waytide/core").SaveOperatorConfigRequest,
): Promise<import("@waytide/core").OperatorConfigResponse> {
  return client.saveOperatorConfig(cfg);
}

// ─── Rules ───────────────────────────────────────────────────────────

/** Read the caller's RESOLVED ruleset (one rule per Operator). With no args,
 * resolves to the global defaults; pass `project` (and optionally `session`)
 * to resolve the most-specific rule in force for that scope. */
export function getRules(
  project?: string,
  session?: string,
): Promise<import("@waytide/core").ResolvedRuleset> {
  return client.getRules(project, session);
}

/** Set one Operator's rule at the given scope and return the saved row. */
export function setRule(
  rule: import("@waytide/core").SetRuleRequest,
): Promise<import("@waytide/core").OperatorRule> {
  return client.setRule(rule);
}

/** Clear one rule by its exact key (Brief 3 D3) — resolution falls back to
 * the broader scope. Idempotent. */
export function clearRule(
  rule: import("@waytide/core").ClearRuleRequest,
): Promise<import("@waytide/core").ClearRuleResponse> {
  return client.clearRule(rule);
}

// ─── Trace ───────────────────────────────────────────────────────────

/** Read a project's Trace (the evidence-linked record behind the timeline).
 * `project` is the agent_session thread id as a string; the read is
 * membership-scoped server-side on the caller's JWT. */
export function getTrace(
  project: string,
): Promise<import("@waytide/core").TraceEvents> {
  return client.getTrace(project);
}

/** Resolve a trace row's `evidence_ref` to the checkable artifact (UC-3 "Wait,
 * why?"). `chat:` resolves inline; `sha:`/`run:` come back `resolved:false`
 * (external). A free read — opening an existing artifact is never billed. */
export function getEvidence(
  project: string,
  ref: string,
): Promise<import("@waytide/core").EvidenceResponse> {
  return client.getEvidence(project, ref);
}

/** Read a project's second brain — the Trace rendered as a markdown
 * knowledge document. The database is the second brain's home; this is its
 * read surface. Same membership scoping as `getTrace`. */
export function getSecondBrain(
  project: string,
): Promise<import("@waytide/core").SecondBrainResponse> {
  return client.getSecondBrain(project);
}

// ─── Radio (Brief 4 / P4a) ───────────────────────────────────────────

/** Read the server-owned radio policy (cap, auto-off, rate). The client renders
 * the cost frame from this — it never invents the numbers. */
export function getRadioPolicy(): Promise<import("@waytide/core").RadioPolicy> {
  return client.getRadioPolicy();
}

/** Read one session's authoritative radio budget (cap / spent / remaining). */
export function getRadioBudget(
  thread: number,
): Promise<import("@waytide/core").RadioBudget> {
  return client.getRadioBudget(thread);
}

/** Synthesize ONE metered radio clip for a session — DEBITS the session budget.
 * Throws ApiError with status 402 ("radio_budget_exhausted") when the cap is
 * reached; the caller turns that into one calm "switch it back on" line. */
export function postRadioClip(
  thread: number,
  spoken: string,
): Promise<import("@waytide/core").RadioClip> {
  return client.postRadioClip(thread, spoken);
}

// ─── Credit balance ──────────────────────────────────────────────────

/** Read the caller's current prepaid credit balance (micros). */
export function getBalance(): Promise<import("@waytide/core").BalanceResponse> {
  return client.getBalance();
}

/** Read per-session spend, summed from the credit ledger. With no `thread` it
 * returns every session with spend; pass a thread id to scope to one
 * agent_session's sub-balance. */
export function getSessionSpend(
  thread?: number,
): Promise<import("@waytide/core").SessionSpendResponse> {
  return client.getSessionSpend(thread);
}

/** Start a Stripe top-up checkout for `amount_usd` and return the checkout url
 * to open in a new tab. The credit posts on the Stripe webhook. */
export function topUpCredit(
  amount_usd: number,
): Promise<import("@waytide/core").TopupCheckoutResponse> {
  return client.topUpCredit(amount_usd);
}

// ─── Project phases + receipts ───────────────────────────────────────

/** List a thread's project phases. `thread` is an agent_session thread id; the
 * read is membership-scoped server-side on the caller's JWT. */
export function listPhases(
  thread: number,
): Promise<import("@waytide/core").PhasesResponse> {
  return client.listPhases(thread);
}

/** Open a new (active) phase on a thread with a title + objective. */
export function openPhase(
  thread_id: number,
  title: string,
  objective: string,
): Promise<import("@waytide/core").Phase> {
  return client.openPhase(thread_id, title, objective);
}

/** Close a phase with a receipt summary; the backend crystallises the receipt
 * from the phase's Trace slice and returns the closed phase + its receipt. */
export function closePhase(
  phase_id: number,
  receipt_summary: string,
): Promise<import("@waytide/core").PhaseWithReceipt> {
  return client.closePhase(phase_id, receipt_summary);
}

/** Read one phase + its receipt (receipt null while the phase is active). */
export function getPhase(
  phase_id: number,
): Promise<import("@waytide/core").PhaseWithReceipt> {
  return client.getPhase(phase_id);
}

// ─── Shadow runs ─────────────────────────────────────────────────────

/** List a thread's shadow runs. `thread` is an agent_session thread id;
 * membership-scoped server-side on the caller's JWT. */
export function listShadowRuns(
  thread: number,
): Promise<import("@waytide/core").ShadowRunsResponse> {
  return client.listShadowRuns(thread);
}

/** Opt a phase into a shadow run with an objective + budget (micros, drawn
 * from the credit balance), returning the server-stamped run. */
export function openShadowRun(
  phase_id: number,
  objective: string,
  budget_micros: number,
): Promise<import("@waytide/core").ShadowRun> {
  return client.openShadowRun(phase_id, objective, budget_micros);
}
